<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs } from 'vue'
import isEmpty from 'lodash/isEmpty'
import FramePatternChildRender from './child/FramePatternChild.vue'
import { useCustomization } from '@/hooks/useCustomization'
import { useEnhancer } from '@/app/enhancer'
import { frameTypes } from '@/hooks/enums'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import CardItem from '@/components/ui/card/CardItem.vue'

export default defineComponent({
  name: 'GoFrame',
  components: { CardHeader, CardItem, FramePatternChildRender },
  emits: ['ok', 'selected', 'back'],
  setup(_, { emit }) {
    const customization = useCustomization()
    const { storeFavorites } = useEnhancer()

    const state = reactive({
      mode: '',
      filtered: '',
    })

    const setMode = (type) => {
      // if (type === 'PATTERN')
      //   return
      state.mode = type
    }

    const handleBack = () => {
      state.mode = ''
    }

    const title = computed(() => {
      const mapping = {
        [frameTypes.COLOR]: 'COLOR FRAME',
        [frameTypes.PATTERN]: 'PATTERN',
      }
      return mapping[state.mode] ?? ''
    })

    const subTitle = computed(() => {
      const mapping = {
        [frameTypes.COLOR]: 'カラーフレーム',
        [frameTypes.PATTERN]: 'パターン',
      }
      return mapping[state.mode] ?? ''
    })

    const defaultFirstFiltered = computed(
      () => customization.getFrameFiltered.value?.[0]?.key,
    )
    const defaultFirstFrameColor = computed(
      () => customization.getFrameColors.value[defaultFirstFiltered.value]?.[0],
    )

    const list = computed(() => {
      return customization.getFrameColors.value?.[state.filtered] ?? []
    })

    const firstFilterItem = computed(() => {
      return list.value?.[0] ?? {}
    })

    const isCurrent = item =>
      item.value === storeFavorites.selectedFrame?.value

    const selected = (item) => {
      if (isCurrent(item))
        return
      storeFavorites.setCurrentConfig('frame', item)
    }

    const currentActive = computed(() => {
      const key = storeFavorites.selectedFrame?.value
      if (key.includes('pattern'))
        return frameTypes.PATTERN
      return frameTypes.COLOR
    })

    const patternList = computed(() => {
      const list = customization.getFramePattern.value.map(item => ({
        ...item,
        url: item.photo.split(',').shift(),
      }))
      return list
    })

    const activeMode = (item) => {
      return item.key === currentActive.value
    }

    const onSelect = (key: string) => {
      state.filtered = key
    }

    // TODO: Temporary solution only active pattern_bekko
    const shouldActivePatternBekko = (item) => {
      if (!item.value.includes('pattern'))
        return true
      const activeKeys = ['pattern_bekko', 'pattern_acetate_bekko', 'pattern_acetate_bekko_yellow']
      return activeKeys.includes(item.value)
    }

    onMounted(() => {
      const selectedFrame = storeFavorites.selectedFrame
      if (isEmpty(selectedFrame)) {
        state.filtered = defaultFirstFiltered.value ?? ''
        selected(defaultFirstFrameColor.value)
      }
      else {
        state.filtered = selectedFrame.description
      }
    })

    return {
      ...toRefs(state),
      title,
      subTitle,
      firstFilterItem,
      list,
      frameTypes,
      frameOptions: customization.getFrameOptions,
      frameFiltered: customization.getFrameFiltered,
      setMode,
      handleBack,
      selected,
      isCurrent,
      activeMode,
      onSelect,
      patternList,
      shouldActivePatternBekko,
      handleClose: () => {
        emit('back')
      },
    }
  },
})
</script>

<template>
  <div v-if="!mode" class="animate_500">
    <CardHeader
      title="フレームデザインを選ぶ"
      hidden-left
      @close="handleClose"
    />
    <div class="row full-height items-center q-col-gutter-md q-px-md q-py-sm">
      <!-- :class="{ 'cursor-pointer': item.key !== 'PATTERN', 'disabled-default cursor-not-allowed': item.key === 'PATTERN' }" -->
      <div
        v-for="item in frameOptions"
        :key="item.key"
        class="col-6 cursor-pointer"
        @click="setMode(item.key)"
      >
        <q-card
          class="relative-position q-py-sm" :class="[{ border: activeMode(item) }]"
        >
          <div
            v-if="activeMode(item)"
            class="absolute-top-right bg-transparent z-top"
            style="padding: 2px 4px"
          >
            <q-avatar
              class="shadow-3"
              color="secondary"
              text-color="white"
              size="sm"
            >
              <q-icon name="checked" size="16px" />
            </q-avatar>
          </div>
          <!-- <q-badge
            v-if="item.key === 'PATTERN'"
            class="absolute-top-left text-body2 text-red text-weight-medium"
            style="z-index: 10; border-radius: 0 0 4px 0"
            color="grey-6"
          >
            改装中
          </q-badge> -->
          <q-img
            style="height: 125px"
            :src="item.url"
            alt="image cover"
            fit="contain"
            no-spinner
            no-transition
            fetchpriority="high"
          >
            <div
              class="
                absolute-center
                text-center text-dark
                bg-transparent
                full-width
              "
            >
              <div class="text-body1 text-weight-medium q-mb-none">
                {{ item.key }}
              </div>
              <p class="text-small q-mb-none">
                {{ item.label }}
              </p>
            </div>
          </q-img>
        </q-card>
      </div>
    </div>
  </div>
  <div v-else class="animate_500 bg-grey-4">
    <CardHeader
      :title="title"
      :description="subTitle"
      @back="handleBack"
      @close="handleClose"
    />
    <div class="full-height">
      <template v-if="mode === frameTypes.COLOR">
        <q-virtual-scroll
          v-slot="{ item }"
          :items="frameFiltered"
          virtual-scroll-horizontal
        >
          <q-btn
            :key="item.key"
            :style="`background: ${item.renderColor}`"
            round
            dense
            size="sm"
            no-wrap
            class="q-mx-md q-my-md" :class="[{ zoom_large: filtered === item.key }]"
            @click="onSelect(item.key)"
          />
        </q-virtual-scroll>
        <q-virtual-scroll
          v-slot="{ item }"
          :items="list"
          virtual-scroll-horizontal
          class="q-px-sm q-pb-xs"
        >
          <div :key="item.value" class="q-pl-xs q-py-xs">
            <CardItem
              :item="item"
              :is-active="isCurrent(item)"
              @selected="selected(item)"
            />
          </div>
        </q-virtual-scroll>
      </template>
      <template v-else-if="mode === frameTypes.PATTERN">
        <q-virtual-scroll
          v-slot="{ item, index }"
          :items="patternList"
          virtual-scroll-horizontal
          class="q-px-md"
        >
          <FramePatternChildRender
            :key="index"
            :item="item"
            :is-current="isCurrent(item)"
            :is-active="shouldActivePatternBekko(item)"
            @selected="selected"
          />
        </q-virtual-scroll>
        <!-- <div class="text-caption text-center q-py-sm full-width">
          裏面には、弊社指定の補助色が入ります。
        </div> -->
      </template>
    </div>
  </div>
</template>
